import React, {useState} from 'react';
import {Link as RouterLink, useLocation, useNavigate} from 'react-router-dom';
// @mui
import {
    alpha,
    AppBar,
    Box,
    Button,
    Collapse,
    Container,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    styled,
    Toolbar,
} from '@mui/material';
import {ChevronRight, ExpandMore, KeyboardArrowDown, Menu as MenuIcon,} from '@mui/icons-material';

// ----------------------------------------------------------------------

const HEADER_MOBILE = 64;
const HEADER_DESKTOP = 72;

const StyledRoot = styled(AppBar)(({theme}) => ({
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: alpha(theme.palette.background.paper, 0.92),
    backdropFilter: 'blur(10px)',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    zIndex: theme.zIndex.appBar + 1,
}));

const StyledToolbar = styled(Toolbar)(({theme}) => ({
    height: HEADER_MOBILE, [theme.breakpoints.up('md')]: {
        height: HEADER_DESKTOP,
    },
}));

const NavItem = styled(Button)(({theme}) => ({
    ...theme.typography.subtitle2, color: theme.palette.text.secondary, padding: theme.spacing(1, 2), '&:hover': {
        color: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
}));

// ----------------------------------------------------------------------

const FEATURES = [{title: 'All features', path: '/home#features'}, {
    title: 'Formulation Lifecycle Manager',
    path: '/features/formulation-lifecycle-manager'
}, {title: 'Formulation Designer', path: '/features/formulation-designer'}, {
    title: 'Ingredient Library', path: '/features/ingredient-library'
}, {title: 'Development Assistant', path: '/features/development-assistant'}, {
    title: 'Management Panel',
    path: '/features/management-panel'
}, {title: 'Data Integration', path: '/features/data-integration'},];

export default function NavigationBar() {
    const {pathname, hash} = useLocation();
    const navigate = useNavigate();
    const [openDrawer, setOpenDrawer] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [mobileFeaturesOpen, setMobileFeaturesOpen] = useState(false);

    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleToggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setOpenDrawer(open);
    };

    const handleToggleMobileFeatures = () => {
        setMobileFeaturesOpen(!mobileFeaturesOpen);
    };

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({behavior: 'smooth'});
        }
    };

    // Handle hash navigation
    React.useEffect(() => {
        if (hash) {
            const id = hash.replace('#', '');
            setTimeout(() => scrollToSection(id), 100);
        }
    }, [hash, pathname]);

    const navConfig = [{title: 'Platform', path: '/home'}, {title: 'Features', path: null, isDropdown: true}, {title: 'Pricing', path: '/pricing'}, {title: 'Data ownership', path: '/home#privacy'}, {title: 'Contact', path: '/home#contact'},];

    const renderLogo = (<Box
        component={RouterLink}
        to="/home"
        sx={{display: 'flex', alignItems: 'center', textDecoration: 'none'}}
    >
        <Box component="img" src="/assets/cibus_B.png" alt="CibusAI" sx={{height: 40}}/>
    </Box>);

    const scrollToTop = () => {
        const main = document.getElementById('main-scroll');

        if (main) {
            main.scrollTo({top: 0, behavior: 'smooth'});
        }

        window.scrollTo({top: 0, behavior: 'smooth'});
        document.documentElement.scrollTo?.({top: 0, behavior: 'smooth'});
        document.body.scrollTo?.({top: 0, behavior: 'smooth'});
    };

    const handlePlatformClick = (event) => {
        event.preventDefault();

        if (pathname === '/home') {
            if (hash) {
                navigate('/home', {replace: true});
            }

            requestAnimationFrame(() => {
                scrollToTop();
            });

            return;
        }

        navigate('/home');

        setTimeout(() => {
            scrollToTop();
        }, 100);
    };

    return (<StyledRoot position="fixed">
        <Container maxWidth="xl">
            <StyledToolbar disableGutters>
                {renderLogo}

                <Box sx={{flexGrow: 1}}/>

                {/* Desktop Navigation */}
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{display: {xs: 'none', md: 'flex'}, mr: 2}}
                >
                    {navConfig.map((item) => {
                        if (item.isDropdown) {
                            return (<React.Fragment key={item.title}>
                                <NavItem
                                    endIcon={<KeyboardArrowDown sx={{ml: -0.5}}/>}
                                    onClick={handleOpenMenu}
                                    sx={{
                                        ...(anchorEl && {
                                            color: 'primary.main',
                                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                                        }),
                                    }}
                                >
                                    {item.title}
                                </NavItem>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleCloseMenu}
                                    anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                                    transformOrigin={{vertical: 'top', horizontal: 'left'}}
                                    PaperProps={{
                                        sx: {
                                            p: 1,
                                            mt: 1.5,
                                            minWidth: 240,
                                            boxShadow: (theme) => theme.customShadows?.z16 || '0 8px 16px 0 rgba(0,0,0,0.1)',
                                            border: (theme) => `1px solid ${theme.palette.divider}`,
                                        },
                                    }}
                                >
                                    {FEATURES.map((feature) => (<MenuItem
                                        key={feature.title}
                                        component={RouterLink}
                                        to={feature.path}
                                        onClick={handleCloseMenu}
                                        sx={{
                                            typography: 'body2',
                                            borderRadius: 0.75,
                                            py: 1, ...(feature.path === '/home#features' && {
                                                fontWeight: 'bold',
                                                color: 'primary.main',
                                                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                                                mb: 0.5,
                                                borderRadius: 0
                                            })
                                        }}
                                    >
                                        {feature.title}
                                    </MenuItem>))}
                                </Menu>
                            </React.Fragment>);
                        }
                        return (<NavItem
                            key={item.title}
                            component={RouterLink}
                            to={item.path}
                            onClick={item.title === 'Platform' ? handlePlatformClick : undefined}
                            aria-current={pathname === item.path ? 'page' : undefined}
                            sx={{
                                ...(pathname === item.path && {
                                    color: 'primary.main',
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                                }),
                            }}
                        >
                            {item.title}
                        </NavItem>);
                    })}
                </Stack>
<Box sx={{display: {xs: 'none', md: 'flex'}}}>
                    <Stack direction={"row"} spacing={3}>
                <Button
                    variant="outlined"
                    component="a"
                    href="mailto:info@omniumai.com"
                    sx={{
                        display: {xs: 'none', sm: 'inline-flex'}, boxShadow: 'none', '&:hover': {boxShadow: 'none'},
                    }}
                >
                    Book a demo
                </Button>


                        <Button
                            variant="contained"
                            component={RouterLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            to="https://app.cibus.omniumai.com/"
                            sx={{
                            display: {xs: 'none', sm: 'inline-flex'},
                            boxShadow: 'none',
                            '&:hover': {boxShadow: 'none'},
                        }}>
                            Sign Up </Button>
                    </Stack>
                </Box>

                {/* Mobile Menu Icon */}
                <IconButton
                    onClick={handleToggleDrawer(true)}
                    sx={{
                        ml: 1, display: {md: 'none'}, color: 'text.primary',
                    }}
                >
                    <MenuIcon/>
                </IconButton>
            </StyledToolbar>
        </Container>

        {/* Mobile Drawer */}
        <Drawer
            anchor="right"
            open={openDrawer}
            onClose={handleToggleDrawer(false)}
            PaperProps={{
                sx: {width: 280},
            }}
        >
            <Box sx={{p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                {renderLogo}
            </Box>

            <List sx={{px: 2}}>
                {navConfig.map((item) => (<React.Fragment key={item.title}>
                    {item.isDropdown ? (<>
                        <ListItemButton onClick={handleToggleMobileFeatures} sx={{borderRadius: 1}}>
                            <ListItemText
                                primary={item.title}
                                primaryTypographyProps={{typography: 'subtitle2'}}
                            />
                            {mobileFeaturesOpen ? <ExpandMore/> : <ChevronRight/>}
                        </ListItemButton>
                        <Collapse in={mobileFeaturesOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding sx={{pl: 2}}>
                                {FEATURES.map((feature) => (<ListItemButton
                                    key={feature.title}
                                    component={RouterLink}
                                    to={feature.path}
                                    onClick={handleToggleDrawer(false)}
                                    sx={{borderRadius: 1}}
                                >
                                    <ListItemText
                                        primary={feature.title}
                                        primaryTypographyProps={{
                                            typography: 'body2',
                                            color: feature.path === '/home#features' ? 'primary.main' : 'text.secondary',
                                            fontWeight: feature.path === '/home#features' ? 'bold' : 'medium'
                                        }}
                                    />
                                </ListItemButton>))}
                            </List>
                        </Collapse>
                    </>) : (<ListItemButton
                        component={RouterLink}
                        to={item.path}
                        onClick={(event) => {
                            handleToggleDrawer(false)(event);

                            if (item.title === 'Platform') {
                                handlePlatformClick(event);
                            }
                        }}
                        aria-current={pathname === item.path ? 'page' : undefined}
                        sx={{borderRadius: 1}}
                    >
                        <ListItemText
                            primary={item.title}
                            primaryTypographyProps={{
                                typography: 'subtitle2',
                                color: pathname === item.path ? 'primary.main' : 'text.primary',
                            }}
                        />
                    </ListItemButton>)}
                </React.Fragment>))}
            </List>

            <Box sx={{p: 3, mt: 'auto'}}>
                <Button
                    fullWidth
                    variant="contained"
                    component="a"
                    href="mailto:info@omniumai.com"
                    size="large"
                >
                    Book a demo
                </Button>
            </Box>
        </Drawer>
    </StyledRoot>);
}
